import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { safeLog } from '@/utils/safeLogger';

// Shared state for debouncing parallel sync calls
let syncPromise: Promise<any> | null = null;
let lastSyncTime = 0;
const SYNC_DEBOUNCE_MS = 2000; // 2 seconds

export interface Subscription {
  id: string;
  user_id: string;
  tier: "starter" | "pro" | "enterprise" | "debug";
  billing_cycle: "monthly" | "annual" | "daily";
  credits_monthly: number;
  credits_remaining: number;
  status: "active" | "cancelled" | "past_due";
  current_period_start: string;
  current_period_end: string;
  created_at: string;
  updated_at: string;
  cancel_at_period_end?: boolean;
  stripe_schedule_id?: string | null;
  scheduled_tier?: string | null;
  scheduled_billing_cycle?: string | null;
  scheduled_change_date?: string | null;
}

interface SubscriptionContextType {
  subscription: Subscription | null;
  isLoading: boolean;
  error: string | null;
  refresh: (syncFromStripe?: boolean) => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSubscription = async (syncFromStripe = false) => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setSubscription(null);
        setIsLoading(false);
        return;
      }

      // First, sync from Stripe if requested (on initial load or manual refresh)
      if (syncFromStripe) {
        const now = Date.now();
        
        try {
          // If a sync is already in progress, wait for it instead of starting a new one
          if (syncPromise) {
            console.log('[SubscriptionContext] Sync already in progress, waiting...');
            await syncPromise;
            syncPromise = null;
          } 
          // If we synced recently, skip
          else if (now - lastSyncTime < SYNC_DEBOUNCE_MS) {
            console.log('[SubscriptionContext] Skipping sync - last sync was recent');
          } 
          // Otherwise start new sync
          else {
            console.log('[SubscriptionContext] Starting new sync...');
            syncPromise = supabase.functions.invoke('check-subscription');
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Sync timeout')), 10000)
            );
            
            await Promise.race([syncPromise, timeoutPromise]);
            lastSyncTime = Date.now();
            console.log('[SubscriptionContext] Sync completed');
          }
        } catch (syncError) {
          console.error("Error syncing from Stripe:", syncError);
          // Continue to load from DB even if sync fails
        } finally {
          syncPromise = null;
        }
      }

      // Load subscriptions that are active OR cancelled but still valid
      const { data, error: fetchError } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .in("status", ["active", "cancelled"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (fetchError) {
        console.error("Error loading subscription:", fetchError);
        setError(fetchError.message);
      } else {
        // Validate: only use if truly active or cancelled with valid period
        if (data) {
          const now = new Date();
          const periodEnd = new Date(data.current_period_end);
          
          // Cancelled but period hasn't ended yet = still valid
          if (data.status === 'cancelled' && now < periodEnd) {
            setSubscription(data as Subscription);
          } else if (data.status === 'active') {
            setSubscription(data as Subscription);
          } else {
            setSubscription(null);
          }
        } else {
          setSubscription(data as Subscription | null);
        }
      }
    } catch (err) {
      console.error("Unexpected error loading subscription:", err);
      setError("Failed to load subscription");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // On initial load, sync from Stripe first
    loadSubscription(true);

    // Subscribe to auth changes
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session?.user) {
          loadSubscription(true); // Also sync on login
        } else {
          setSubscription(null);
        }
      }
    );

    return () => {
      authSubscription.unsubscribe();
    };
  }, []);

  return (
    <SubscriptionContext.Provider value={{ 
      subscription, 
      isLoading, 
      error,
      refresh: loadSubscription 
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
};
